/**
 * Local Schedules Service - Irrigo Platform
 * Servicio para gestión de programaciones locales en controladoras
 */

import { api, ApiResponse } from './api';
import type {
  ApiLocalSchedule,
  ApiLocalSchedulesResponse,
  CreateLocalScheduleRequest,
  UpdateLocalScheduleRequest,
  ApplyScheduleRequest,
  ApplyScheduleResponse,
  SyncSchedulesRequest,
  SyncSchedulesResponse,
} from '@/types/api';

// ============================================================================
// LOCAL SCHEDULES SERVICE
// ============================================================================

export const localSchedulesService = {
  /**
   * Listar programaciones locales de una controladora
   */
  async getByController(controllerId: string): Promise<ApiResponse<ApiLocalSchedulesResponse>> {
    return api.get<ApiLocalSchedulesResponse>(`/controllers/${controllerId}/local-schedules`);
  },

  /**
   * Crear nueva programación local
   */
  async create(
    controllerId: string,
    data: CreateLocalScheduleRequest
  ): Promise<ApiResponse<ApiLocalSchedule>> {
    return api.post<ApiLocalSchedule>(`/controllers/${controllerId}/local-schedules`, data);
  },

  /**
   * Actualizar programación local existente.
   * Intenta PUT /local-schedules/{id}; si 404, fallback a ruta por controladora.
   */
  async update(
    controllerId: string,
    scheduleId: string,
    data: UpdateLocalScheduleRequest
  ): Promise<ApiResponse<ApiLocalSchedule>> {
    const response = await api.put<ApiLocalSchedule>(`/local-schedules/${scheduleId}`, data);
    if (!response.success && response.error?.code === 'HTTP_404') {
      return api.put<ApiLocalSchedule>(
        `/controllers/${controllerId}/local-schedules/${scheduleId}`,
        data
      );
    }
    return response;
  },

  /**
   * Eliminar programación local (por id de programación).
   * Intenta DELETE /local-schedules/{id}; si 404, fallback a ruta por controladora.
   */
  async deleteById(
    scheduleId: string,
    controllerId?: string
  ): Promise<ApiResponse<{ message: string }>> {
    const response = await api.delete<{ message: string }>(`/local-schedules/${scheduleId}`);
    if (!response.success && controllerId && response.error?.code === 'HTTP_404') {
      return api.delete<{ message: string }>(
        `/controllers/${controllerId}/local-schedules/${scheduleId}`
      );
    }
    return response;
  },

  /**
   * Eliminar programación local (ruta bajo controladora, compatibilidad).
   * Use deleteById(scheduleId) si el backend expone DELETE /local-schedules/{id}
   */
  async delete(
    controllerId: string,
    scheduleId: string
  ): Promise<ApiResponse<{ message: string }>> {
    return api.delete<{ message: string }>(
      `/controllers/${controllerId}/local-schedules/${scheduleId}`
    );
  },

  /**
   * Activar o desactivar programación local (endpoint dedicado).
   * Según doc backend: POST /local-schedules/{id}/toggle con body { enabled }
   */
  async toggle(
    scheduleId: string,
    enabled: boolean
  ): Promise<ApiResponse<ApiLocalSchedule>> {
    return api.post<ApiLocalSchedule>(`/local-schedules/${scheduleId}/toggle`, { enabled });
  },

  /**
   * Aplicar programación a múltiples controladoras
   */
  async applyToMultiple(data: ApplyScheduleRequest): Promise<ApiResponse<ApplyScheduleResponse>> {
    return api.post<ApplyScheduleResponse>('/local-schedules/apply', data);
  },

  /**
   * Sincronizar programaciones con el dispositivo
   */
  async sync(
    controllerId: string,
    data?: SyncSchedulesRequest
  ): Promise<ApiResponse<SyncSchedulesResponse>> {
    return api.post<SyncSchedulesResponse>(
      `/controllers/${controllerId}/local-schedules/sync`,
      data
    );
  },

  /**
   * Sincronizar una programación individual con el dispositivo.
   * El backend incluye timezone + hora antes de enviar el plan.
   */
  async syncById(scheduleId: string): Promise<ApiResponse<ApiLocalSchedule>> {
    return api.post<ApiLocalSchedule>(`/local-schedules/${scheduleId}/sync`);
  },

  /**
   * Consultar programaciones del dispositivo
   * Envía comandos al dispositivo para obtener las programaciones configuradas localmente
   */
  async fetch(controllerId: string): Promise<ApiResponse<{ message: string }>> {
    return api.post<{ message: string }>(`/controllers/${controllerId}/local-schedules/fetch`);
  },

  /**
   * Consultar programaciones de TODAS las controladoras en modo local
   * Solo disponible para superadmin
   */
  async fetchAll(): Promise<ApiResponse<{
    message: string;
    successCount: number;
    errorCount: number;
  }>> {
    return api.post<{
      message: string;
      successCount: number;
      errorCount: number;
    }>('/controllers/local-schedules/fetch');
  },
};

export default localSchedulesService;
